/**
 * Hook equivalents of appSelectors from Redux store
 * These hooks read from AppConfigContext instead of Redux
 *
 * Migration pattern:
 * Old: useAppSelector(appSelectors.getBusinessError)
 * New: useBusinessErrors()
 */

import { EFinancingProgram } from '@if/core/types'
import { keyBy } from 'lodash-es'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppConfigContext } from '../../contexts/app-config-context'
import { Constants, ValueListItem } from '../types'
import FeatureFlags from '../types/FeatureFlags'
import { FinancingConfigDto } from '../types/FinancingConfigDto'

// ============================================
// Error Selectors
// ============================================

/**
 * Get business errors
 * Replaces: appSelectors.getBusinessError
 */
export function useBusinessErrors() {
  const { businessErrors } = useAppConfigContext()
  return businessErrors
}

/**
 * Get technical error
 * Replaces: appSelectors.getTechnicalError
 */
export function useTechnicalError() {
  const { globalTechnicalError } = useAppConfigContext()
  return globalTechnicalError
}

/**
 * Set business errors
 * Replaces: dispatch(appActions.setBusinessErrors(...))
 */
export function useSetBusinessErrors() {
  const { setBusinessErrors } = useAppConfigContext()
  return setBusinessErrors
}

/**
 * Set technical error
 * Replaces: dispatch(appActions.setTechnicalError(...))
 */
export function useSetTechnicalError() {
  const { setTechnicalError } = useAppConfigContext()
  return setTechnicalError
}

/**
 * Reset all errors
 * Replaces: dispatch(appActions.resetErrors())
 */
export function useResetErrors() {
  const { resetErrors } = useAppConfigContext()
  return resetErrors
}

// ============================================
// Language Selectors
// ============================================

/**
 * Get current language
 * Replaces: appSelectors.getCurrentLang
 */
export type SupportedLanguage = 'en' | 'fr'

export function useCurrentLang(): SupportedLanguage {
  const {
    i18n: { resolvedLanguage },
  } = useTranslation()
  return (resolvedLanguage as SupportedLanguage) ?? 'en'
}

// ============================================
// Enum Selectors
// ============================================

/**
 * Get job type enum, filtered by financing program
 * Replaces: appSelectors.getJobTypeEnum(state, financingProgramId)
 */
export function useJobTypeEnum(financingProgramId: EFinancingProgram | null = null) {
  const { jobTypeEnum } = useAppConfigContext()

  return useMemo(() => {
    let supportsUnavailableJobType = false
    if (financingProgramId) {
      supportsUnavailableJobType = Constants.financingProgramConfigs[financingProgramId].supportsUnavailableJobType
    }
    const filteredEnum = { ...jobTypeEnum }
    if (!supportsUnavailableJobType) {
      delete filteredEnum[Constants.UnavailableJobTypeId]
    }
    return filteredEnum
  }, [jobTypeEnum, financingProgramId])
}

/**
 * Get other income types enum
 * Replaces: appSelectors.getOtherIncomeTypesEnum
 */
export function useOtherIncomeTypesEnum() {
  const { otherIncomeTypesEnum } = useAppConfigContext()
  return otherIncomeTypesEnum
}

/**
 * Get applicant type enum
 * Replaces: appSelectors.getApplicantTypeEnum
 */
export function useApplicantTypeEnum() {
  const { applicantTypeEnum } = useAppConfigContext()
  return applicantTypeEnum
}

/**
 * Get financing program enum
 * Replaces: appSelectors.getFinancingProgramEnum
 */
export function useFinancingProgramEnum() {
  const { financingProgramEnum } = useAppConfigContext()
  return financingProgramEnum
}

/**
 * Get plan enum for a specific financing program
 * Replaces: appSelectors.getPlanEnumForProgram(state, financingProgram)
 */
export function usePlanEnumForProgram(financingProgram?: EFinancingProgram | null) {
  const {
    autoPlanEnums,
    ifxPlanEnums,
    personalLoanPlanEnums,
    b2cPlanEnums,
    creditRslaPlanEnums,
  } = useAppConfigContext()

  return useMemo(() => {
    switch (financingProgram) {
      case EFinancingProgram.Auto:
        return autoPlanEnums
      case EFinancingProgram.IfXpress:
        return ifxPlanEnums
      case EFinancingProgram.Personal:
        return personalLoanPlanEnums
      case EFinancingProgram.B2c:
        return b2cPlanEnums
      case EFinancingProgram.CreditRsla:
        return creditRslaPlanEnums
      case EFinancingProgram.AutoRsla:
      case EFinancingProgram.Products:
        return {}
      default:
        return {
          ...autoPlanEnums,
          ...ifxPlanEnums,
          ...personalLoanPlanEnums,
          ...b2cPlanEnums,
          ...creditRslaPlanEnums,
        }
    }
  }, [financingProgram, autoPlanEnums, ifxPlanEnums, personalLoanPlanEnums, b2cPlanEnums, creditRslaPlanEnums])
}

/**
 * Get all plans for multiple financing programs
 * Replaces: appSelectors.getAllPlansForPrograms(state, programs)
 */
export function useAllPlansForPrograms(programs: EFinancingProgram[]) {
  const {
    autoPlanEnums,
    ifxPlanEnums,
    personalLoanPlanEnums,
    b2cPlanEnums,
    creditRslaPlanEnums,
  } = useAppConfigContext()

  return useMemo(() => {
    return programs.flatMap((program) => {
      switch (program) {
        case EFinancingProgram.Auto:
          return Object.values(autoPlanEnums || {})
        case EFinancingProgram.IfXpress:
          return Object.values(ifxPlanEnums || {})
        case EFinancingProgram.Personal:
          return Object.values(personalLoanPlanEnums || {})
        case EFinancingProgram.B2c:
          return Object.values(b2cPlanEnums || {})
        case EFinancingProgram.CreditRsla:
          return Object.values(creditRslaPlanEnums || {})
        case EFinancingProgram.AutoRsla:
        case EFinancingProgram.Products:
          return []
        default:
          return []
      }
    })
  }, [programs, autoPlanEnums, ifxPlanEnums, personalLoanPlanEnums, b2cPlanEnums, creditRslaPlanEnums])
}

// ============================================
// Document Type Selectors
// ============================================

/**
 * Get document types, optionally filtered by financing program
 * Replaces: appSelectors.getDocumentTypes(state, financingProgramId)
 */
export function useDocumentTypes(financingProgramId: EFinancingProgram | null = null) {
  const { documentTypes } = useAppConfigContext()

  return useMemo(() => {
    if (financingProgramId === null) return documentTypes
    return keyBy(
      Object.values(documentTypes).filter(
        (x) =>
          x.restrictedToFinancingProgramIds.includes(financingProgramId) ||
          x.restrictedToFinancingProgramIds.length === 0,
      ),
      'id',
    )
  }, [documentTypes, financingProgramId])
}

/**
 * Get document types that can be added manually
 * Replaces: appSelectors.getDocumentTypesAddableManually(state, financingProgramId)
 */
export function useDocumentTypesAddableManually(financingProgramId: EFinancingProgram) {
  const { documentTypes } = useAppConfigContext()

  return useMemo(() => {
    return keyBy(
      Object.values(documentTypes).filter(
        (x) =>
          x.canBeAddedManually === true &&
          (x.restrictedToFinancingProgramIds.includes(financingProgramId) ||
            x.restrictedToFinancingProgramIds.length === 0),
      ),
      'id',
    )
  }, [documentTypes, financingProgramId])
}

// ============================================
// Task Type Selectors
// ============================================

/**
 * Get task types, optionally filtered by financing program
 * Replaces: appSelectors.getTaskTypes(state, financingProgramId)
 */
export function useTaskTypes(financingProgramId: EFinancingProgram | null = null) {
  const { taskTypes } = useAppConfigContext()

  return useMemo(() => {
    if (financingProgramId === null) return taskTypes
    return keyBy(
      Object.values(taskTypes).filter(
        (x) =>
          x.restrictedToFinancingProgramIds.includes(financingProgramId) ||
          x.restrictedToFinancingProgramIds.length === 0,
      ),
      'id',
    )
  }, [taskTypes, financingProgramId])
}

/**
 * Get task types that can be added manually
 * Replaces: appSelectors.getTaskTypesAddableManually(state, financingProgramId)
 */
export function useTaskTypesAddableManually(financingProgramId: EFinancingProgram) {
  const { taskTypes } = useAppConfigContext()

  return useMemo(() => {
    return keyBy(
      Object.values(taskTypes).filter(
        (x) =>
          x.canBeAddedManually === true &&
          (x.restrictedToFinancingProgramIds.includes(financingProgramId) ||
            x.restrictedToFinancingProgramIds.length === 0),
      ),
      'id',
    )
  }, [taskTypes, financingProgramId])
}

/**
 * Get all task types for a financing program
 * Replaces: appSelectors.getAllTaskTypes(state, financingProgramId)
 */
export function useAllTaskTypes(financingProgramId: EFinancingProgram) {
  const { taskTypes } = useAppConfigContext()

  return useMemo(() => {
    return keyBy(
      Object.values(taskTypes).filter(
        (x) =>
          x.restrictedToFinancingProgramIds.includes(financingProgramId) ||
          x.restrictedToFinancingProgramIds.length === 0,
      ),
      'id',
    )
  }, [taskTypes, financingProgramId])
}

// ============================================
// Config Data Selectors
// ============================================

/**
 * Get territories list
 * Replaces: appSelectors.getTerritoryList
 */
export function useTerritories() {
  const { territories } = useAppConfigContext()
  return territories
}

/**
 * Get product providers
 * Replaces: appSelectors.getProductProviders
 */
export function useProductProviders() {
  const { productsProviders } = useAppConfigContext()
  return productsProviders
}

/**
 * Get mechanical warranty provider
 * Replaces: appSelectors.getMechanicalWarrantyProvider
 */
export function useMechanicalWarrantyProvider() {
  const { mechanicalWarrantyProvider } = useAppConfigContext()
  return mechanicalWarrantyProvider
}

/**
 * Get province configurations
 * Replaces: appSelectors.getProvinceConfigurations
 */
export function useProvinceConfigurations() {
  const { provinceConfigurations } = useAppConfigContext()
  return provinceConfigurations
}

/**
 * Get iFinance holidays
 * Replaces: appSelectors.getHolidays
 */
export function useHolidays() {
  const { iFinanceHolidays } = useAppConfigContext()
  return iFinanceHolidays
}

/**
 * Get financing config for a specific program
 * Replaces: appSelectors.getFinancingConfig(state, financingProgram)
 */
export function useFinancingConfig(financingProgram?: EFinancingProgram | null): FinancingConfigDto {
  const { financingConfigs } = useAppConfigContext()

  return useMemo(() => {
    return (
      financingConfigs.find((x) => x.financingProgramId === financingProgram) ?? {
        effectiveRateThreshold: 0,
        largeLoanAmount: 0,
        longTerm: 0,
        maximumLoanAmount: 0,
        minimumLoanAmount: 0,
        useCreditReport: true,
      } as FinancingConfigDto
    )
  }, [financingConfigs, financingProgram])
}

/**
 * Programs whose applications can be copied as a draft, and that a copy can be moved to
 */
export function useProgramsSupportingCopyAsDraft(): EFinancingProgram[] {
  const { financingConfigs } = useAppConfigContext()

  return useMemo(
    () => financingConfigs.filter((x) => x.supportsCopyAsDraft).map((x) => x.financingProgramId),
    [financingConfigs],
  )
}

// ============================================
// Value List Selectors
// ============================================

/**
 * Helper to build value list from DTO
 */
function buildValueListFromDto(list: { items: { value: string; textFr: string; textEn: string; priority?: number }[]; sortMethod: string } | null, lang: string): ValueListItem[] {
  if (!list) return []

  const inputList = [...list.items]
  if (list.sortMethod === 'priority') inputList.sort((x) => x.priority ?? 0)

  const items: ValueListItem[] = inputList.map((x) => ({
    id: x.value,
    text: lang === 'fr' ? x.textFr : x.textEn,
  }))
  if (list.sortMethod === 'alpha') items.sort((one, two) => (one.text > two.text ? -1 : 1))
  return items
}

/**
 * Get credit refusal reasons as formatted list
 * Replaces: appSelectors.getCreditRefusalReasons
 */
export function useCreditRefusalReasons(): ValueListItem[] {
  const { creditRefusalReasons } = useAppConfigContext()
  const lang = useCurrentLang()

  return useMemo(() => buildValueListFromDto(creditRefusalReasons, lang), [creditRefusalReasons, lang])
}

/**
 * Get merchant tags as formatted list
 * Replaces: appSelectors.getMerchantTags
 */
export function useMerchantTags(): ValueListItem[] {
  const { merchantTags } = useAppConfigContext()
  const lang = useCurrentLang()

  return useMemo(() => buildValueListFromDto(merchantTags, lang), [merchantTags, lang])
}

/**
 * Get merchant tag keys
 * Replaces: appSelectors.getMerchantTagKeys
 */
export function useMerchantTagKeys(): string[] {
  const tags = useMerchantTags()
  return useMemo(() => tags.map((tag) => tag.id), [tags])
}

// ============================================
// Feature Flag Selectors
// ============================================

/**
 * Get feature switches info
 * Replaces: appSelectors.getFeatureSwitches
 */
export function useFeatureSwitches() {
  const { featureSwitchInfo } = useAppConfigContext()
  return featureSwitchInfo
}

/**
 * Get feature flags helper class
 * Replaces: appSelectors.getFeatureFlags
 */
export function useFeatureFlags(): FeatureFlags {
  const { featureSwitchInfo } = useAppConfigContext()

  return useMemo(() => {
    if (!featureSwitchInfo?.featureSwitches) {
      return new FeatureFlags([])
    }
    return new FeatureFlags(featureSwitchInfo.featureSwitches)
  }, [featureSwitchInfo])
}

// ============================================
// Misc Selectors
// ============================================

/**
 * Get conflict state
 * Replaces: appSelectors.conflict
 */
export function useConflict() {
  const { conflict } = useAppConfigContext()
  return conflict
}

/**
 * Set conflict state
 * Replaces: dispatch(appActions.setConflict(...))
 */
export function useSetConflict() {
  const { setConflict } = useAppConfigContext()
  return setConflict
}

/**
 * Get app loading state
 * Replaces: appSelectors.isLoading
 */
export function useIsAppLoading() {
  const { isInitializing } = useAppConfigContext()
  return isInitializing
}

/**
 * Get deprecated state
 * Replaces: appSelectors.isDeprecated
 */
export function useIsDeprecated() {
  const { isDeprecated } = useAppConfigContext()
  return isDeprecated
}

/**
 * Set deprecated state
 * Replaces: dispatch(appActions.setDeprecated(...))
 */
export function useSetDeprecated() {
  const { setDeprecated } = useAppConfigContext()
  return setDeprecated
}

/**
 * Get active and inactive financing programs
 * Replaces: appSelectors.getActiveAndInactiveFinancingPrograms
 */
export function useActiveAndInactiveFinancingPrograms(financingProgram?: EFinancingProgram | null) {
  return useMemo(() => {
    switch (financingProgram) {
      case EFinancingProgram.Auto:
        return { activePrograms: [], inactivePrograms: [EFinancingProgram.Auto] }
      case EFinancingProgram.Personal:
        return { activePrograms: [EFinancingProgram.Personal], inactivePrograms: [] }
      case EFinancingProgram.B2c:
        return { activePrograms: [EFinancingProgram.B2c], inactivePrograms: [] }
      case EFinancingProgram.IfXpress:
        return { activePrograms: [EFinancingProgram.IfXpress], inactivePrograms: [] }
      case EFinancingProgram.Products:
        return { activePrograms: [EFinancingProgram.Products], inactivePrograms: [] }
      case EFinancingProgram.AutoRsla:
        return { activePrograms: [EFinancingProgram.AutoRsla], inactivePrograms: [] }
      case EFinancingProgram.CreditRsla:
        return { activePrograms: [EFinancingProgram.CreditRsla], inactivePrograms: [] }
      case EFinancingProgram.MechanicalWarranty:
        return { activePrograms: [EFinancingProgram.MechanicalWarranty], inactivePrograms: [] }
      default:
        return { activePrograms: [], inactivePrograms: [] }
    }
  }, [financingProgram])
}
